'use client';

import * as React from 'react';
import { DiscoverCountSkeleton } from '@/components/atoms/DiscoverCountSkeleton';
import { cn } from '@/components/atoms/primitives/utils';
import { formatCount } from '@/lib/discover/formatCount';

/**
 * Sentinel slug emitted by `onToggle` when the header "Clear" link is
 * activated. Callers can either intercept this sentinel (e.g. clear the URL
 * search param entirely) or supply an explicit `onClear` handler.
 */
export const FACET_CLEAR_ALL = '__loci_facet_clear_all__';

export interface DiscoverFacetChecklistItem {
  /** URL-safe slug, also the value emitted by `onToggle`. */
  slug: string;
  /** Visible label. */
  label: string;
  /** Count rendered right-aligned. `0` is rendered with reduced contrast. */
  count: number;
}

/**
 * Row state handed to {@link DiscoverFacetChecklistViewProps.formatCountLabel}.
 * `isChecked` — row is in `selected`; `isDisabled` — row is a zero-count row
 * suppressed by `disableZeroCount`; `hasSelection` — any row in this facet is
 * selected, i.e. this dimension is currently filtering.
 */
export interface DiscoverFacetRowState {
  isChecked: boolean;
  isDisabled: boolean;
  hasSelection: boolean;
}

/** Formatter for a row's trailing count caption (WEB-828). */
export type DiscoverFacetCountLabelFormatter = (
  item: DiscoverFacetChecklistItem,
  state: DiscoverFacetRowState,
) => string | undefined;

export interface DiscoverFacetChecklistViewProps {
  /** Header title (e.g. "Category"). */
  title: string;
  /** Facet rows. Render order is preserved. */
  items: ReadonlyArray<DiscoverFacetChecklistItem>;
  /** Currently selected slugs. */
  selected: ReadonlyArray<string>;
  /** Called once per row activation with the row's slug. */
  onToggle: (slug: string) => void;
  /**
   * Called when the header "Clear" link is activated. If omitted, the
   * component falls back to `onToggle(FACET_CLEAR_ALL)` so callers using
   * URL state can intercept the sentinel.
   */
  onClear?: () => void;
  /** Optional id used to associate the `<fieldset>` legend with the group. */
  id?: string;
  /** Optional override for the visually-hidden `aria-label` on each input. */
  ariaLabelForItem?: (item: DiscoverFacetChecklistItem) => string;
  /**
   * Optional callback returning a stable DOM id for each row's checkbox
   * input. When provided, the input renders with `id={inputIdForItem(item)}`
   * and the wrapping label sets `htmlFor` for an explicit association,
   * silencing React's form-field accessibility heuristic. Returning
   * `undefined` (the default) preserves the legacy implicit (wrapping)
   * label association with no `id` attribute. Callers should namespace the
   * id (e.g. `category-${slug}`, `neighborhood-${slug}`) so collisions are
   * impossible across filters.
   *
   * @see WEB-265 — Category rail form-field warning fix.
   * @see WEB-264 — Neighborhood rail consumer.
   */
  inputIdForItem?: (item: DiscoverFacetChecklistItem) => string | undefined;
  /**
   * When `true`, zero-count rows render disabled instead of a clickable
   * `(0)` row — non-interactive to both pointer and keyboard, and exposing
   * `aria-disabled="true"`. Defaults to `false` so existing consumers (e.g.
   * `DiscoverNeighborhoodFilter`) keep today's dimmed-but-clickable
   * behavior unchanged. Set by `DiscoverCategoryFilter` (WEB-659 / DPL-09) —
   * a category with zero events tonight is a dead end, not a real filter
   * choice, so the row is disabled rather than offering a toggle that can
   * only ever return nothing.
   *
   * A row that is BOTH selected (in the URL) and zero-count (e.g. a
   * date-preset change dropped a previously-picked category to zero) still
   * renders `checked` + `disabled` — the selection is preserved, not
   * silently cleared, but it can no longer be toggled from within this row.
   * Removal happens via the separate `DiscoverActiveFilters` chip strip,
   * which reads URL state directly and is unaffected by this prop.
   *
   * @see WEB-659
   */
  disableZeroCount?: boolean;
  /**
   * Caption rendered in place of the numeric `0` for a disabled zero-count
   * row (e.g. `"None tonight"`, `"No active events"`). Only read when
   * {@link disableZeroCount} is `true`. Falls back to `"No active events"`
   * when omitted so the row never renders visually blank.
   */
  zeroCountLabel?: string;
  /**
   * Optional per-row override for the trailing count caption (WEB-828).
   * Returning a string renders it verbatim in the caption slot using the
   * mono treatment; returning `undefined` falls through to the
   * default rendering (the numeric count, or {@link zeroCountLabel} for a
   * disabled zero-count row), so a consumer can override some rows and not
   * others.
   *
   * Exists because a bare count is ambiguous once a filter in this same
   * dimension is active: with `?category=music` selected, `Art (12)` does not
   * mean "12 results" — those 12 are precisely the events being filtered OUT.
   * `DiscoverCategoryFilter` uses this to say `Showing 12 events` /
   * `12 hidden events` instead. A single formatter (rather than more boolean
   * props) keeps this view's other consumer — `DiscoverNeighborhoodFilter` —
   * on the unchanged default path.
   *
   * Never called while {@link countsPending} is `true`; a skeleton renders
   * instead, since there is no honest count to describe yet.
   *
   * @param item The row being rendered.
   * @param state `isChecked` — row is in `selected`; `isDisabled` — row is a
   *   zero-count row suppressed by {@link disableZeroCount}; `hasSelection` —
   *   any row in this facet is selected, i.e. this dimension is filtering.
   */
  formatCountLabel?: DiscoverFacetCountLabelFormatter;
  /**
   * When set, only the first `visibleLimit` items (in the order supplied via
   * `items`) render by default. Any item beyond that slice that is currently
   * `selected` still renders — collapsing the list must never hide the
   * viewer's own active filter (WEB-660). A "show all / show less"
   * affordance renders beneath the list whenever the full item count
   * exceeds `visibleLimit`; activating it toggles between the truncated and
   * full list. Omit to render every item unconditionally — the legacy
   * behavior, and the Category consumer's default (its list is always
   * exactly six items, so it never truncates either way).
   */
  visibleLimit?: number;
  /**
   * Formats the show-all/show-less affordance label. Called with the total
   * (untruncated) item count — always the live `items.length`, never a
   * hardcoded value — and whether the list is currently expanded. Only
   * consulted when `visibleLimit` triggers truncation; a generic English
   * fallback ("Show all N" / "Show less") is used when omitted.
   */
  formatExpandLabel?: (totalCount: number, expanded: boolean) => string;
  /**
   * WEB-786 — facet counts have not resolved yet. Row labels stay so the
   * rail remains usable; numeric counts skeleton instead of painting a
   * placeholder `0` that later becomes non-zero. Empty `items` + pending
   * renders skeleton rows instead of "No options available".
   */
  countsPending?: boolean;
  /**
   * WEB-946 / DES-79 — right-side scope chrome on the section head
   * (`Upcoming · CLT` / `Tonight`). Built by the caller from
   * {@link formatDiscoverCategoryHeadScope} /
   * {@link formatDiscoverNeighborhoodHeadScope} so Category and Neighborhood
   * share the results-bar vocabulary. Omitted on other facet lists.
   */
  headScope?: string;
  /** Wrapper class names. */
  className?: string;
}

const DEFAULT_ZERO_COUNT_LABEL = 'No active events';
const PENDING_SKELETON_ROWS = 6;

const SCROLL_THRESHOLD = 8;
// Scroll cap for long lists: about 8 rows of the pre-v3.1 44px rhythm. Rows
// are 36px now (WEB-1076), so it shows a little more; the cap is unchanged.
const SCROLL_MAX_HEIGHT = 'max-h-[352px]';

export function DiscoverFacetChecklistView({
  title,
  items,
  selected,
  onToggle,
  onClear,
  id,
  ariaLabelForItem,
  inputIdForItem,
  disableZeroCount = false,
  zeroCountLabel = DEFAULT_ZERO_COUNT_LABEL,
  formatCountLabel,
  visibleLimit,
  formatExpandLabel,
  countsPending = false,
  headScope,
  className,
}: DiscoverFacetChecklistViewProps): React.ReactElement {
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const hasTruncation = typeof visibleLimit === 'number' && items.length > visibleLimit;

  // The truncated (default) slice is the first `visibleLimit` items in
  // caller-supplied order, plus any already-selected item that would
  // otherwise fall outside it — a quiet neighborhood the viewer has already
  // filtered to must not vanish from the collapsed rail just because its
  // count dropped (WEB-660).
  const visibleItems = React.useMemo(() => {
    if (!hasTruncation || isExpanded) return items;
    const topSlice = items.slice(0, visibleLimit);
    const topSlugs = new Set(topSlice.map((item) => item.slug));
    const selectedOverflow = items
      .slice(visibleLimit)
      .filter((item) => selectedSet.has(item.slug) && !topSlugs.has(item.slug));
    return selectedOverflow.length > 0 ? [...topSlice, ...selectedOverflow] : topSlice;
  }, [hasTruncation, isExpanded, items, selectedSet, visibleLimit]);

  const isScrollable = visibleItems.length > SCROLL_THRESHOLD;
  const legendId = id ? `${id}-legend` : undefined;

  const handleClear = React.useCallback(() => {
    if (onClear) {
      onClear();
      return;
    }
    onToggle(FACET_CLEAR_ALL);
  }, [onClear, onToggle]);

  const handleToggleExpanded = React.useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const hasSelection = selected.length > 0;

  return (
    <fieldset
      data-testid="discover-facet-checklist"
      className={cn('flex flex-col gap-2', className)}
      aria-labelledby={legendId}
    >
      <legend id={legendId} className="sr-only">
        {title}
      </legend>
      <header className="flex items-center justify-between gap-2 px-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <div className="flex min-w-0 items-center gap-2">
          {headScope ? (
            <span
              data-testid="discover-facet-head-scope"
              className="min-w-0 break-words text-right font-mono text-micro font-normal tracking-[0.12em] text-muted-foreground"
            >
              {headScope}
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleClear}
            disabled={!hasSelection}
            data-testid="discover-facet-checklist-clear"
            className={cn(
              // WEB-1076: a 32px text link like the sheet's "More" link; `.a-hit-floor`
              // grows its hit box to 44×44 on coarse pointers only.
              'a-hit-floor inline-flex min-h-8 desktop:min-h-0 items-center justify-center px-2 py-1 text-xs font-medium underline-offset-2 transition-colors',
              'disabled:cursor-not-allowed disabled:text-muted-foreground/50 disabled:no-underline',
              'enabled:text-clt-skyline enabled:hover:underline enabled:focus-visible:underline',
              'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline focus-visible:ring-offset-2',
            )}
          >
            Clear
          </button>
        </div>
      </header>

      {items.length === 0 && countsPending ? (
        <>
          {/*
            The one announcing layer for this pending cluster. Kept outside
            the list (a `role="status"` on an `<li>` would drop it out of the
            list's own semantics) and outside every row, so AT hears the
            pending state once rather than once per skeleton.
          */}
          <span className="sr-only" role="status">
            Loading filter options
          </span>
          <ul
            data-testid="discover-facet-checklist-pending"
            className="flex flex-col"
            aria-busy="true"
          >
            {Array.from({ length: PENDING_SKELETON_ROWS }, (_, index) => (
              <li key={index} className="flex min-h-9 items-center gap-3 px-2 py-2">
                <span
                  aria-hidden="true"
                  className="size-4.5 shrink-0 animate-pulse rounded-[3px] bg-muted motion-reduce:animate-none"
                />
                {/*
                  Unlabelled: the sr-only status above speaks for the whole
                  list. Labelling each would make six rows × two skeletons
                  twelve live regions announcing the same thing.
                */}
                <DiscoverCountSkeleton className="w-24" label={null} />
                <DiscoverCountSkeleton className="ml-auto w-6" label={null} />
              </li>
            ))}
          </ul>
        </>
      ) : items.length === 0 ? (
        <p
          data-testid="discover-facet-checklist-empty"
          className="px-1 py-3 text-sm text-muted-foreground"
        >
          No options available
        </p>
      ) : (
        <>
          {/*
            Rows are present but their counts are not. Same rule as the
            empty-list branch: ONE status for the whole pending cluster,
            every row skeleton muted — otherwise a 23-neighborhood list
            announces "Loading count" 23 times.
          */}
          {countsPending ? (
            <span className="sr-only" role="status">
              Loading filter counts
            </span>
          ) : null}
          <ul
            data-testid="discover-facet-checklist-items"
            aria-busy={countsPending ? 'true' : undefined}
            className={cn(
              // `.dv-facet-list`: an 8px row gap on coarse pointers only, so
              // the 36px rows' 44px guards never overlap a neighbour's paint
              // (styles/components/discover-facets.css, WEB-1076).
              'dv-facet-list flex flex-col',
              // `py-1`: the scrollport clips overflow, so the first and last
              // 36px rows need 4px of room for the 44px coarse-pointer guard
              // (`.a-hit-floor::before`) to stay tappable (WEB-1076).
              isScrollable && cn('overflow-y-auto py-1 pr-1', SCROLL_MAX_HEIGHT),
            )}
          >
            {visibleItems.map((item) => {
              const isChecked = selectedSet.has(item.slug);
              const isZeroCount = !countsPending && item.count === 0;
              const isRowDisabled = disableZeroCount && isZeroCount;
              const inputId = inputIdForItem ? inputIdForItem(item) : undefined;
              // WEB-828 — not consulted while counts are pending: there is no
              // honest number to describe yet, so the skeleton branch wins.
              const countLabel =
                !countsPending && formatCountLabel
                  ? formatCountLabel(item, { isChecked, isDisabled: isRowDisabled, hasSelection })
                  : undefined;
              // A caller-supplied label wins over the zero-state caption; both
              // render through the same span below.
              const caption = countLabel ?? (isRowDisabled ? zeroCountLabel : undefined);
              return (
                <li key={item.slug} className="contents">
                  <label
                    data-testid={`discover-facet-checklist-row-${item.slug}`}
                    data-checked={isChecked ? 'true' : 'false'}
                    data-disabled={isRowDisabled ? 'true' : 'false'}
                    htmlFor={inputId}
                    aria-disabled={isRowDisabled ? 'true' : undefined}
                    className={cn(
                      // WEB-1076 / FRE-1379: a 36px row at every width and on both
                      // pointers (the rail kept 36px at desktop before this, too);
                      // the 44px touch floor is the coarse-pointer `.a-hit-floor`
                      // `::before`, spaced off its neighbours by `.dv-facet-list`.
                      'a-hit-floor group flex min-h-9 items-center gap-3 rounded-md px-2 py-2 text-sm transition-colors',
                      isRowDisabled
                        ? 'cursor-not-allowed'
                        : 'cursor-pointer hover:bg-clt-skyline-wash focus-within:bg-clt-skyline-wash',
                    )}
                  >
                    <input
                      type="checkbox"
                      id={inputId}
                      className="sr-only"
                      checked={isChecked}
                      disabled={isRowDisabled}
                      onChange={() => onToggle(item.slug)}
                      aria-label={ariaLabelForItem ? ariaLabelForItem(item) : item.label}
                    />
                    {/* WEB-828: a disabled row must LOOK disabled. The box
                        previously kept the enabled `border-input bg-background`
                        treatment AND the `group-hover:border-clt-skyline`
                        highlight, so an unavailable option invited a click it
                        would then refuse — the viewer only discovered the row
                        was dead by pressing it. Disabled now reads as a filled,
                        dashed, non-reactive box. Checked-and-disabled keeps the
                        skyline fill (the selection is real and preserved) but
                        drops to a muted border so it is distinguishable from an
                        actionable checked row. */}
                    <span
                      aria-hidden="true"
                      data-testid={`discover-facet-checklist-box-${item.slug}`}
                      className={cn(
                        // WEB-1076 / FRE-1379: 18px visual box inside a 36px row.
                        'relative flex size-4.5 shrink-0 items-center justify-center rounded-[3px] border transition-colors',
                        "after:pointer-events-none after:absolute after:left-1/2 after:top-1/2 after:h-[7px] after:w-[11px] after:-translate-x-1/2 after:-translate-y-[60%] after:-rotate-45 after:rounded-[1px] after:border-b-2 after:border-l-2 after:border-white after:opacity-0 after:transition-opacity after:content-['']",
                        // Fill and border are independent axes, so they read as
                        // two flat ternaries rather than one nested tree.
                        // Checked-and-disabled keeps the SOLID skyline fill: a
                        // translucent one (`/40`) drops the white tick to
                        // ~1.8:1 against the composited light theme fill
                        // (#0e7b7b @40% over the rail), and that is the one
                        // state WEB-659 exists to keep legible — it is how a
                        // viewer sees that a still-applied `?category=` is what
                        // emptied the list.
                        isChecked
                          ? 'bg-clt-skyline after:opacity-100'
                          : isRowDisabled
                            ? 'bg-muted'
                            : 'bg-background',
                        // The dashed muted border and the dead hover carry the
                        // disabled signal on both checked and unchecked rows.
                        isRowDisabled
                          ? 'border-dashed border-muted-foreground/40'
                          : isChecked
                            ? 'border-clt-skyline'
                            : 'border-input group-hover:border-clt-skyline',
                      )}
                    />
                    {/* WEB-745: do not dim 14px label text with opacity —
                        `opacity-60` on `--foreground` (#1E2B4B) composites
                        to #788093 on white (3.95:1). `--loci-color-text-muted`
                        (#6B7280) is 4.82:1 and still reads as the dimmed
                        zero-count / disabled treatment (WEB-659 / DPL-09). */}
                    {/* WEB-791 / PAR-39: the label WRAPS, it does not truncate.
                        `flex-1 truncate` ellipsised the longest canonical
                        category to `Hobbies & Int…` in the 288px rail once the
                        `NONE TONIGHT` zero-state caption claimed the row's
                        trailing space — a facet the viewer cannot read is not a
                        filter. The 36px row is a MINIMUM, so a second line is
                        free; wrapping also cannot overflow the rail the way a
                        removed truncate on a fixed-width child would. */}
                    <span
                      className={cn(
                        'min-w-0 flex-1 break-words',
                        isRowDisabled || isZeroCount ? 'text-muted-foreground' : 'text-foreground',
                      )}
                    >
                      {item.label}
                    </span>
                    {countsPending ? (
                      // Unlabelled: one live region per facet row would make a
                      // 23-neighborhood list announce "Loading count" 23
                      // times. The row's own label already names the option.
                      <DiscoverCountSkeleton
                        data-testid={`discover-facet-checklist-count-skeleton-${item.slug}`}
                        className="w-6 shrink-0"
                        label={null}
                      />
                    ) : caption !== undefined ? (
                      // Caption slot: a caller-supplied WEB-828 label
                      // (`Showing 12 events` / `12 hidden events`) when there
                      // is one, else the zero-state
                      // caption for a disabled row. Rendering both through a
                      // single span keeps the treatment from drifting between
                      // them.
                      <span
                        data-testid={`discover-facet-checklist-count-${item.slug}`}
                        className={cn(
                          'shrink-0 font-mono text-micro tracking-wide',
                          isRowDisabled || isZeroCount
                            ? 'text-muted-foreground/60'
                            : 'text-muted-foreground',
                        )}
                      >
                        {caption}
                      </span>
                    ) : (
                      <span
                        data-testid={`discover-facet-checklist-count-${item.slug}`}
                        className={cn(
                          'shrink-0 font-mono text-micro tabular-nums tracking-wide',
                          isZeroCount ? 'text-muted-foreground/60' : 'text-muted-foreground',
                        )}
                      >
                        {formatCount(item.count)}
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
          {hasTruncation && (
            <button
              type="button"
              onClick={handleToggleExpanded}
              aria-expanded={isExpanded}
              data-testid="discover-facet-checklist-expand"
              className={cn(
                // WEB-1076: the sheet's 32px "More" link, hit guard on coarse pointers.
                'a-hit-floor self-start inline-flex min-h-8 desktop:min-h-0 items-center rounded-sm px-2 py-1 text-xs font-semibold tracking-wide text-clt-skyline transition-colors',
                'hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clt-skyline focus-visible:ring-offset-2',
              )}
            >
              {formatExpandLabel
                ? formatExpandLabel(items.length, isExpanded)
                : isExpanded
                  ? 'Show less'
                  : `Show all ${formatCount(items.length)}`}
            </button>
          )}
        </>
      )}
    </fieldset>
  );
}
